import type { RumInitConfiguration } from '@datadog/browser-rum';

import type { AppEnv, FormConfig } from './env';
import { getAppEnv, getFormConfig } from './env';

type CommonSettings = {
  videoConsult: {
    consultationFeedbackUrl: string;
    contactUsUrl: string;
    privacyPolicyUrl: string;
    termsConditionsUrl: string;
  };
};

type DataDogConfig = { env: string } & Pick<
  RumInitConfiguration,
  'sessionSampleRate' | 'sessionReplaySampleRate' | 'allowedTracingUrls'
>;

type EnvSpecificSettings = {
  instantConnectPhoneNumber: string;
  url: string;
  baseUrl: string;
  xApiUrl: string;
  patientPortalUrl: string;
  circuitLoginUrl: string;
  alternaLeafUrl: string;
  calendar: {
    calLinkNurse: string;
    calLinkDoctor: string;
    calLinkDoctorWA: string;
    calLinkDoctorUnder25: string;
    calLinkD2D: string;
    calOrigin: string;
    embedJsUrl: string;
  };
  paymentsUrl: string;
  scheduleLaterUrl: string;
  preConsultUrl: string;
  preScreeningUrl: string;
  featureFlagSDKKey: string;
  datadog?: DataDogConfig;
  launchDarklyConfig: {
    clientSideID: string;
  };
  googlePlacesApiKey: string;
  connect: {
    instanceConfigUrl: string;
    chat: {
      chatInterfaceClientUrl: string;
      widgetId: string;
      snippetId: string;
    };
  };
};

type GoogleTagManagerSettings = {
  googleTagManagerId: string;
  googleTagManagerAuth: string;
  googleTagManagerPreview?: string;
};

type SnowplowSettings = {
  appId: string;
  trackerName: string;
};

type GoogleTagManagerEnvironments = 'staging' | 'prod';

const common: CommonSettings = {
  videoConsult: {
    consultationFeedbackUrl: 'https://survey.survicate.com/081f8e536a2b71de/',
    contactUsUrl:
      'https://support.alternaleaf.com.au/hc/en-au/articles/34017993658905-Contact-Alternaleaf?_gl=1*1l1uk82*_gcl_au*NjcxNDUzNzA1LjE3NjQyMTE0NjU.',
    privacyPolicyUrl: 'https://www.alternaleaf.com.au/privacy',
    termsConditionsUrl: 'https://www.alternaleaf.com.au/terms-of-service',
  },
};

export type EnvSettings = CommonSettings & EnvSpecificSettings;

const development: EnvSettings = {
  ...common,
  instantConnectPhoneNumber: '+61881337442',
  url: 'https://pms.staging.alternaleaf.com.au/api/v1',
  baseUrl: 'https://pms.staging.alternaleaf.com.au',
  xApiUrl: 'https://pms.staging.alternaleaf.com.au/api',
  patientPortalUrl: 'https://patient.staging.alternaleaf.com.au',
  circuitLoginUrl: 'https://patient.staging.alternaleaf.com.au/login',
  alternaLeafUrl: 'https://alternaleaf-v2.webflow.io',
  scheduleLaterUrl: 'https://pre-screening.staging.alternaleaf.com.au/?cal=true',
  calendar: {
    calLinkNurse: 'team/alternaleaf-nurses/initial-consultation',
    calLinkDoctor: 'team/alternaleaf-doctors/medical-cannabis-consultation',
    calLinkDoctorWA: 'team/alternaleaf-doctors/medical-cannabis-consultation-wa',
    calLinkDoctorUnder25: 'team/alternaleaf-doctors/medical-cannabis-consultation-25',
    calLinkD2D: 'team/alternaleaf-doctors/medical-consultation-d2d',
    calOrigin: 'https://bookings.staging.alternaleaf.com.au',
    embedJsUrl: 'https://bookings.staging.alternaleaf.com.au/embed/embed.js',
  },
  paymentsUrl: 'https://pms.staging.alternaleaf.com.au/card-detail',
  preConsultUrl: 'https://pre-consult.staging.alternaleaf.com.au',
  preScreeningUrl: 'https://pre-screening.staging.alternaleaf.com.au',
  featureFlagSDKKey: '19b40cc5-ac82-4051-a4eb-7395df64132d',
  launchDarklyConfig: {
    clientSideID: '64867d6a8ed097125e3ca762',
  },
  googlePlacesApiKey: 'AIzaSyDAdfaMqM0q7jQ6deJ0nsWzEIZB8xfpXpI',
  connect: {
    instanceConfigUrl: 'https://montu-clinic-nonprod.my.connect.aws/ccp-v2-config/config.json',
    chat: {
      chatInterfaceClientUrl:
        'https://montu-clinic-nonprod.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
      widgetId: 'e3af98d0-812f-4044-95b5-b45681466e05',
      snippetId:
        'QVFJREFIZ0JXUDRpTEE4cmZyV2xXczFoK1FVRnFIUjZkUU9mYTlTR2FNYytKUWduQXdFdUJrS2xSbUUyVWVYeWNVTXFDbjNiQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNY3Q4YjEvSXpodkl3QjFuUkFnRVFnQ3RwQ2hicmhFQlpNSlVIQkplUXNmaU9zaWxGcGdnZDh2N2FjNnoxcjhnbEJtOHFWenBMOTN1UUhTVng6Om9uZ25qcHREeDdnZVlTRlM2VmUrd3VKWE1aSkxoTEl1S1F4WCttYjJSaGtWTkE1ajJsM3pnYlpBVjJHNlNGbXpwbUpzUzM3SXd4eUhhYnVHN2NTRy9lMmdLS1MxeVI3SnpzQnB0UUYrLzJ6UzhKalc4YVR3VDdkU0VFa1hkVFBIQXJGTFhSM3c2b0JYRjBCTHJ0bHZpZVdMV291eXdXMD0=',
    },
  },
};

const developmentFullStack: EnvSettings = {
  ...development,

  url: 'http://localhost:9000/api/v1',
  baseUrl: 'http://localhost:9000',
  xApiUrl: 'http://localhost:9000/api',
};

const staging: EnvSettings = {
  ...common,
  instantConnectPhoneNumber: '+61881337442',
  url: 'https://pms.staging.alternaleaf.com.au/api/v1',
  baseUrl: 'https://pms.staging.alternaleaf.com.au',
  xApiUrl: 'https://pms.staging.alternaleaf.com.au/api',
  patientPortalUrl: 'https://patient.staging.alternaleaf.com.au',
  circuitLoginUrl: 'https://patient.staging.alternaleaf.com.au/login',
  alternaLeafUrl: 'https://alternaleaf-v2.webflow.io',
  scheduleLaterUrl: 'https://pre-screening.staging.alternaleaf.com.au/?cal=true',
  datadog: {
    env: 'staging',
    sessionSampleRate: 100, // percentage of sessions capturing RUM
    sessionReplaySampleRate: 0, // percentage of sessions capturing RUM, AND session replays
    allowedTracingUrls: ['https://pms.staging.alternaleaf.com.au/api'],
  },
  calendar: {
    calLinkNurse: 'team/alternaleaf-nurses/initial-consultation',
    calLinkDoctor: 'team/alternaleaf-doctors/medical-cannabis-consultation',
    calLinkDoctorWA: 'team/alternaleaf-doctors/medical-cannabis-consultation-wa',
    calLinkDoctorUnder25: 'team/alternaleaf-doctors/medical-cannabis-consultation-25',
    calLinkD2D: 'team/alternaleaf-doctors/medical-consultation-d2d',
    calOrigin: 'https://bookings.staging.alternaleaf.com.au',
    embedJsUrl: 'https://bookings.staging.alternaleaf.com.au/embed/embed.js',
  },
  paymentsUrl: 'https://pms.staging.alternaleaf.com.au/card-detail',
  preConsultUrl: 'https://pre-consult.staging.alternaleaf.com.au',
  preScreeningUrl: 'https://pre-screening.staging.alternaleaf.com.au',
  featureFlagSDKKey: '482a7d8a-b4ae-46ba-96d3-1f781ff0ba6f',
  launchDarklyConfig: {
    clientSideID: '64867d63ab4da812c04f22c8',
  },
  googlePlacesApiKey: 'AIzaSyDAdfaMqM0q7jQ6deJ0nsWzEIZB8xfpXpI',
  connect: {
    instanceConfigUrl: 'https://montu-clinic-nonprod.my.connect.aws/ccp-v2-config/config.json',
    chat: {
      chatInterfaceClientUrl:
        'https://montu-clinic-nonprod.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
      widgetId: 'e3af98d0-812f-4044-95b5-b45681466e05',
      snippetId:
        'QVFJREFIZ0JXUDRpTEE4cmZyV2xXczFoK1FVRnFIUjZkUU9mYTlTR2FNYytKUWduQXdFdUJrS2xSbUUyVWVYeWNVTXFDbjNiQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNY3Q4YjEvSXpodkl3QjFuUkFnRVFnQ3RwQ2hicmhFQlpNSlVIQkplUXNmaU9zaWxGcGdnZDh2N2FjNnoxcjhnbEJtOHFWenBMOTN1UUhTVng6Om9uZ25qcHREeDdnZVlTRlM2VmUrd3VKWE1aSkxoTEl1S1F4WCttYjJSaGtWTkE1ajJsM3pnYlpBVjJHNlNGbXpwbUpzUzM3SXd4eUhhYnVHN2NTRy9lMmdLS1MxeVI3SnpzQnB0UUYrLzJ6UzhKalc4YVR3VDdkU0VFa1hkVFBIQXJGTFhSM3c2b0JYRjBCTHJ0bHZpZVdMV291eXdXMD0=',
    },
  },
};

const production: EnvSettings = {
  ...common,
  instantConnectPhoneNumber: '+61737404815',
  url: 'https://pms.alternaleaf.com.au/api/v1',
  baseUrl: 'https://pms.alternaleaf.com.au',
  xApiUrl: 'https://pms.alternaleaf.com.au/api',
  patientPortalUrl: 'https://patient.alternaleaf.com.au',
  circuitLoginUrl: 'https://patient.alternaleaf.com.au/login',
  alternaLeafUrl: 'https://www.alternaleaf.com.au',
  scheduleLaterUrl: 'https://pre-screening.alternaleaf.com.au/?cal=true',
  datadog: {
    env: 'production', // was initially prod, updated to be in line with other datadog services
    sessionSampleRate: 100, // percentage of sessions capturing RUM
    sessionReplaySampleRate: 50, // percentage of sessions capturing RUM, AND session replays,
    allowedTracingUrls: ['https://pms.alternaleaf.com.au/api'],
  },
  calendar: {
    calLinkNurse: 'team/alternaleaf-nurses/initial-consultation',
    calLinkDoctor: 'team/alternaleaf-doctors/medical-cannabis-consultation',
    calLinkDoctorWA: 'team/alternaleaf-doctors/medical-cannabis-consultation-wa',
    calLinkDoctorUnder25: 'team/alternaleaf-doctors/medical-cannabis-consultation-25',
    calLinkD2D: 'team/alternaleaf-doctors/medical-consultation-d2d',
    calOrigin: 'https://bookings.alternaleaf.com.au',
    embedJsUrl: 'https://bookings.alternaleaf.com.au/embed/embed.js',
  },
  paymentsUrl: 'https://pms.alternaleaf.com.au/card-detail',
  preConsultUrl: 'https://pre-consult.alternaleaf.com.au',
  preScreeningUrl: 'https://pre-screening.alternaleaf.com.au',
  featureFlagSDKKey: '5a1e0251-5c72-4b00-80bd-a0f6e7c75b6b',
  launchDarklyConfig: {
    clientSideID: '64867d5b62b9df124bee5656',
  },
  googlePlacesApiKey: 'AIzaSyBVFk2BtSVOPoORJf3bq7T_JVbfv-z_5zE',
  connect: {
    instanceConfigUrl: 'https://montu-clinic-prod.my.connect.aws/ccp-v2-config/config.json',
    chat: {
      chatInterfaceClientUrl:
        'https://montu-clinic-prod.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
      widgetId: '23093346-146d-4f6b-9448-20bdbd9858ba',
      snippetId:
        'QVFJREFIZ0JXUDRpTEE4cmZyV2xXczFoK1FVRnFIUjZkUU9mYTlTR2FNYytKUWduQXdHMGFRMnpOa1NjYTdzZnlFdnM4ZHhJQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNK0hPTnVpdm55QWN6TXpud0FnRVFnQ3ZiU0tiRUNqV0h6aWl4anB6bHJCeHR4U24xSjZ0T1UzU2FYa1NVS0JWZ2ZTWEtWTEQzNEtqdlJKUFI6OnNJWFpHMTdMVmVGTnRFRFBhdEo4bVdVdDFNTmpGU1luclRMMjJaTThVdURXZXZCKzFYOVhHTSszRWoyTmtZQ0JwSFlwZElEQ2Yxa3NIT1VwUS8wYWY1NnJoUHZzdGp1dEg1SWVQeXdnVDR6NUs3aG5zM1hQVXRYYk1ib2pOSDE0djlSQmZpRzF5Vm1ITmZHd0x5Yk5lc3owSzVYL2ZwOD0=',
    },
  },
};

const screeningGoogleTagManager: GoogleTagManagerSettings = {
  googleTagManagerId: 'GTM-WBQ3Q27',
  googleTagManagerAuth: '',
  googleTagManagerPreview: '',
};

const consultGoogleTagManager: GoogleTagManagerSettings = {
  googleTagManagerId: 'GTM-MWGJK4N',
  googleTagManagerAuth: '',
};

const eligibleGoogleTagManager: GoogleTagManagerSettings = {
  googleTagManagerId: '',
  googleTagManagerAuth: '',
};

const portalGoogleTagManager: GoogleTagManagerSettings = {
  googleTagManagerId: 'GTM-WZ6PK5RC',
  googleTagManagerAuth: '',
};

const googleTagManager: Record<GoogleTagManagerEnvironments, Partial<Record<FormConfig, GoogleTagManagerSettings>>> = {
  staging: {
    screening: {
      ...screeningGoogleTagManager,
      googleTagManagerAuth: '1otSZZSy8hbJNJ8DPdhT3Q',
      googleTagManagerPreview: 'env-27',
    },
    consult: { ...consultGoogleTagManager, googleTagManagerAuth: 'v9pl99wz7Li-6GECYTVmJg' },
    portal: { ...portalGoogleTagManager, googleTagManagerAuth: 'b483Q-QluKpvgR3KboIkoA' },
    all: { ...portalGoogleTagManager, googleTagManagerAuth: 'b483Q-QluKpvgR3KboIkoA' },
    eligible: { ...eligibleGoogleTagManager },
  },
  prod: {
    screening: { ...screeningGoogleTagManager, googleTagManagerAuth: '' },
    consult: { ...consultGoogleTagManager, googleTagManagerAuth: '' },
    portal: { ...portalGoogleTagManager, googleTagManagerAuth: '' },
    all: { ...portalGoogleTagManager, googleTagManagerAuth: '' },
    eligible: { ...eligibleGoogleTagManager },
  },
};

function getGoogleTagManagerEnvironment(env: AppEnv): GoogleTagManagerEnvironments {
  return env === 'production' ? 'prod' : 'staging';
}

function mergeGoogleTagManagerSettings(settings: EnvSettings, env: AppEnv): EnvSettings {
  const gtmEnv = getGoogleTagManagerEnvironment(env);
  const formConfig = getFormConfig();

  if (formConfig && googleTagManager[gtmEnv][formConfig]) {
    return { ...settings, ...googleTagManager[gtmEnv][formConfig] };
  }

  return settings;
}

export const snowplowSettings: SnowplowSettings = {
  appId: 'montu.forms',
  trackerName: 'hoc.montu-web/apps/forms',
};

function getEnvironmentSettings(): EnvSettings {
  const env: AppEnv = getAppEnv();
  const baseSettings: Record<AppEnv, EnvSettings> = {
    development,
    developmentFullStack,
    staging,
    production,
  };

  return mergeGoogleTagManagerSettings(baseSettings[env] || development, env);
}

export const SUBMIT_PRESCREENING_POST_URL = `/patient-portal/v1/pre-screening-form`;
export const EMAIL_FALLBACK_ERROR = 'error-referencing-email';

const settings: EnvSettings = getEnvironmentSettings();
export default settings;
